import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, Alert, Box,
} from '@mui/material';
import { cogsService } from '../../services/cogsService';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

/**
 * Set / clear a human override for a SKU's computed lead time (restock or production).
 * The override is stored server-side in cogs_overrides; the computed value is never lost —
 * clearing the override simply re-exposes the computed value.
 *
 * Props:
 *  - open, onClose
 *  - brandId, sku
 *  - regime: 'restock' | 'production' (preset by the cell that opened the dialog)
 *  - currentComputed: the computed lead-time days for this regime (shown for reference)
 *  - hasOverride: whether an override is currently set (enables the Clear button)
 *  - onSaved: called after a successful set/clear so the parent can refetch
 */
export default function LeadTimeOverrideDialog({
  open, onClose, brandId, sku, regime, currentComputed, hasOverride, onSaved,
}) {
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const metric = regime === 'production' ? 'lead_time_production' : 'lead_time_restock';

  // Reset the form each time the dialog opens for a (possibly different) SKU/regime.
  useEffect(() => {
    if (open) {
      setValue('');
      setNote('');
      setError(null);
      setBusy(false);
    }
  }, [open, sku, regime]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await cogsService.setLeadTimeOverride(brandId, { sku, metric, value: Number(value), note });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to save override.', { requestText: false }));
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    setError(null);
    try {
      await cogsService.clearLeadTimeOverride(brandId, { sku, metric });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to clear override.', { requestText: false }));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Override {regime} lead time</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          SKU <strong>{sku}</strong> — computed {regime} lead time:{' '}
          {currentComputed != null && currentComputed !== '' ? `${currentComputed} days` : '—'}
        </Typography>
        <TextField
          fullWidth
          type="number"
          label="Override (days)"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputProps={{ min: 0 }}
          autoFocus
          sx={{ mt: 1 }}
        />
        <TextField
          fullWidth
          label="Why (note, optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ mt: 2 }}
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Clearing the override restores the computed value — the computed figure is never lost.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="warning" onClick={clear} disabled={busy || !hasOverride}>
          Clear override
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={busy || value === '' || Number(value) < 0 || isNaN(Number(value))}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
